import { Component, OnDestroy, OnInit } from "@angular/core";
import { PageBreadcrumbComponent } from "../../../shared/components/common/page-breadcrumb/page-breadcrumb.component";
import {
  PageResponse,
  Segment,
  SegmentFolder,
  SegmentSearchFilters,
} from "../models/segment.model";
import { SegmentService } from "../services/segment-api.service";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { FolderApiService } from "../services/folder-api.service";
import { FolderFormModalComponent } from "../folder-form-modal/folder-form-modal.component";
import { CommonModule } from "@angular/common";
import { debounceTime, Subject, takeUntil, forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { SegmentStatsComponent } from "./components/segment-stats/segment-stats.component";
import { SegmentTableComponent } from "./components/segment-table/segment-table.component";
import { SegmentDetailModalComponent } from "./components/segment-detail-modal/segment-detail-modal.component";
import { ConfirmDialogService } from "../../../shared/services/confirm-dialog.service";
import { ToastService } from "../../../shared/services/toast.service";
import { AuthService } from "../../auth-pages/services/auth.service";
import { HasPermissionDirective } from "../../../shared/directives/has-permission.directive";
import { DisableWithoutPermissionDirective } from "../../../shared/directives/disable-without-permission.directive";

@Component({
  standalone: true,
  selector: "app-segment-list",
  imports: [
    PageBreadcrumbComponent,
    RouterModule,
    FolderFormModalComponent,
    FormsModule,
    CommonModule,
    SegmentStatsComponent,
    SegmentTableComponent,
    SegmentDetailModalComponent,
    HasPermissionDirective,
    DisableWithoutPermissionDirective,
  ],
  templateUrl: "./segment-list.component.html",
})
export class SegmentListComponent implements OnInit, OnDestroy {
  segments: Segment[] = [];
  selectedSegmentsIds: number[] = [];
  folders: SegmentFolder[] = [];

  filterType:
    | "ALL"
    | "DYNAMIC"
    | "STATIC"
    | "SUPPRESSED"
    | "ARCHIVED" = "ALL";
  searchTerm: string = "";
  selectedFolderId: number | null = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  isFolderModalOpen: boolean = false;
  isMoveFolderModalOpen: boolean = false;
  moveFolderTargetId: number | null = null;
  isLoading: boolean = false;

  page: number = 0;
  size: number = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  businessId: number = 0;
  totalElements: number = 0;
  totalPages: number = 0;

  totalSegmentsCount: number = 0;
  dynamicSegmentsCount: number = 0;
  staticSegmentsCount: number = 0;
  suppressedSegmentsCount: number = 0;

  selectedSegment: Segment | null = null;
  isDetailModalOpen: boolean = false;

  constructor(
    private segmentService: SegmentService,
    private folderApi: FolderApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Negocio del usuario autenticado (la clave localStorage('bussinesId')
    // nunca se escribe en la app, por eso antes caía siempre al fallback 1).
    this.businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.cargarCarpetas();
    this.cargarSegmentos();
    this.cargarContadores();

    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        this.page = 0;
        this.cargarSegmentos();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==============================
  // CARGAR DATOS
  // ==============================

  cargarCarpetas(): void {
    this.folderApi.getFolders(0, 50).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.folders = res.content || [];
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudieron cargar las carpetas.' });
      },
    });
  }

  cargarSegmentos(): void {
    this.isLoading = true;

    const filters: SegmentSearchFilters = {};

    if (this.filterType === "DYNAMIC") {
      filters.type = "DYNAMIC";
    } else if (this.filterType === "STATIC") {
      filters.type = "STATIC";
    } else if (this.filterType === "SUPPRESSED") {
      filters.status = "SUPPRESSED";
    } else if (this.filterType === "ARCHIVED") {
      filters.status = "ARCHIVED";
    }

    if (this.searchTerm?.trim()) {
      filters.searchTerm = this.searchTerm.trim();
    }

    if (this.selectedFolderId) {
      filters.folderId = this.selectedFolderId;
    }

    this.segmentService.getSegments(this.page, this.size, filters).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: PageResponse<Segment>) => {
        this.segments = response.content || [];
        this.totalElements = response.totalElements;
        this.totalPages = response.totalPages;

        this.selectedSegmentsIds = [];
        this.isLoading = false;
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudieron cargar los segmentos.' });
        this.isLoading = false;
      },
    });
  }

  private cargarContadores(): void {
    this.segmentService.getStats().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.totalSegmentsCount = res.total;
        this.dynamicSegmentsCount = res.dynamic;
        this.staticSegmentsCount = res.staticCount;
        this.suppressedSegmentsCount = res.suppressed;
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudieron cargar los contadores del módulo.' });
      },
    });
  }

  // ==================
  // SELECCION MULTIPLE
  // ==================

  toggleSelection(id: number): void {
    const index = this.selectedSegmentsIds.indexOf(id);
    if (index === -1) {
      this.selectedSegmentsIds.push(id);
    } else {
      this.selectedSegmentsIds.splice(index, 1);
    }
  }

  toggleAll(event: Event): void {
    if ((event.target as HTMLInputElement).checked) {
      this.selectedSegmentsIds = this.segments.map((s) => s.id);
    } else {
      this.selectedSegmentsIds = [];
    }
  }

  // =======================
  // FILTRADO Y BUSQUEDA
  // =======================

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  setFilterType(
    type:
      | "ALL"
      | "DYNAMIC"
      | "STATIC"
      | "SUPPRESSED"
      | "ARCHIVED",
  ): void {
    this.filterType = type;
    this.page = 0;
    this.cargarSegmentos();
  }

  setFolderFilter(folderId: number | null): void {
    this.selectedFolderId = folderId;
    this.page = 0;
    this.cargarSegmentos();
  }

  // ======================
  // PAGINACION
  // ======================

  previousPage(): void {
    if (this.page > 0) {
      this.page--;
      this.cargarSegmentos();
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages - 1) {
      this.page++;
      this.cargarSegmentos();
    }
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page) return;
    this.page = page;
    this.cargarSegmentos();
  }

  changePageSize(size: number): void {
    this.size = size;
    this.page = 0;
    this.cargarSegmentos();
  }

  // ======================
  // NAVEGACION
  // ======================

  nuevoSegment(): void {
    this.router.navigate(["/segmentos/crear"]);
  }

  editarSegment(id: number): void {
    this.router.navigate(["/segmentos", id, "editar"]);
  }

  verSegment(id: number): void {
    const segment = this.segments.find((s) => s.id === id);
    if (!segment) return;
    this.selectedSegment = segment;
    this.isDetailModalOpen = true;
  }

  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedSegment = null;
    this.cargarSegmentos();
    this.cargarContadores();
  }

  onMemberCountChanged(event: { segmentId: number; totalContacts: number }): void {
    const seg = this.segments.find(s => s.id === event.segmentId);
    if (seg) seg.totalContacts = event.totalContacts;
  }

  async eliminarSegment(id: number): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar segmento',
      message: '¿Estás seguro de que deseas eliminar este segmento? Esta acción no se puede deshacer.',
      confirmText: 'Sí, eliminar'
    });
    if (!ok) return;
    this.segmentService.deleteSegment(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.cargarSegmentos();
        this.cargarContadores();
        this.toast.deleted('Segmento');
      },
      error: (err) => {
        const msg = err?.error?.message || 'No se pudo eliminar el segmento.';
        this.toast.error({ title: 'No se pudo eliminar', message: msg });
      },
    });
  }

  async eliminarSeleccionados(): Promise<void> {
    const count = this.selectedSegmentsIds.length;
    if (count === 0) return;

    const label = count === 1 ? '1 segmento' : `${count} segmentos`;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar segmentos seleccionados',
      message: `¿Estás seguro de que deseas eliminar ${label}? Esta acción no se puede deshacer.`,
      confirmText: `Sí, eliminar ${label}`,
    });
    if (!ok) return;

    const ids = [...this.selectedSegmentsIds];
    const requests = ids.map(id =>
      this.segmentService.deleteSegment(id).pipe(
        map(() => ({ id, ok: true })),
        catchError(() => of({ id, ok: false }))
      )
    );

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe({
      next: (results) => {
        const succeeded = results.filter(r => r.ok).length;
        const failed = results.filter(r => !r.ok).length;
        this.cargarSegmentos();
        this.cargarContadores();
        this.cargarCarpetas();
        if (succeeded > 0 && failed === 0) {
          this.toast.success({
            title: 'Segmentos eliminados',
            message: `Se eliminaron ${succeeded === 1 ? '1 segmento' : `${succeeded} segmentos`} correctamente.`,
          });
        } else if (succeeded > 0 && failed > 0) {
          this.toast.error({
            title: 'Eliminación parcial',
            message: `${succeeded} eliminado(s), ${failed} no se pudo(n) eliminar.`,
          });
        } else {
          this.toast.error({ title: 'Error al eliminar', message: 'No se pudieron eliminar los segmentos seleccionados.' });
        }
      },
    });
  }

  async cambiarEstado(event: { id: number; newStatus: string; action: string }): Promise<void> {
    const config: Record<string, { label: string; confirmText: string; variant: 'danger' | 'warning' | 'success' | 'info' }> = {
      archivar:  { label: 'archivar',   confirmText: 'Archivar',   variant: 'danger'  },
      inactivar: { label: 'inactivar',  confirmText: 'Inactivar',  variant: 'warning' },
      activar:   { label: 'activar',    confirmText: 'Activar',    variant: 'success' },
      restaurar: { label: 'restaurar',  confirmText: 'Restaurar',  variant: 'info'    },
    };
    const { label, confirmText, variant } = config[event.action] ?? { label: 'cambiar estado de', confirmText: 'Confirmar', variant: 'info' as const };

    const ok = await this.confirmDialog.confirm({
      title: 'Cambiar estado',
      message: `¿Estás seguro de que deseas ${label} este segmento?`,
      confirmText,
      variant,
    });
    if (!ok) return;

    this.segmentService
      .updateStatus(event.id, event.newStatus as 'DRAFT' | 'READY' | 'ARCHIVED' | 'SUPPRESSED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.cargarSegmentos();
          this.cargarContadores();
          this.toast.updated('Segmento');
        },
        error: () => {
          this.toast.error({ title: 'Error', message: 'No se pudo actualizar el estado del segmento.' });
        },
      });
  }

  duplicarSegment(id: number): void {
    this.segmentService.duplicateSegment(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.cargarSegmentos();
        this.cargarCarpetas();
        this.toast.success({ title: 'Segmento duplicado', message: 'Se creó una copia del segmento correctamente.' });
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudo duplicar el segmento.' });
      },
    });
  }

  // Move to folder modal
  openMoveFolderModal(): void {
    this.isMoveFolderModalOpen = true;
    this.moveFolderTargetId = null;
  }

  closeMoveFolderModal(): void {
    this.isMoveFolderModalOpen = false;
    this.moveFolderTargetId = null;
  }

  moveSelectedToFolder(): void {
    if (this.selectedSegmentsIds.length === 0) return;

    const ids = [...this.selectedSegmentsIds];
    const requests = ids.map(id =>
      this.segmentService.moveToFolder(id, this.moveFolderTargetId).pipe(
        map(() => ({ id, ok: true })),
        catchError(() => of({ id, ok: false }))
      )
    );

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe({
      next: (results) => {
        const succeeded = results.filter(r => r.ok).length;
        const failed = results.filter(r => !r.ok).length;
        this.closeMoveFolderModal();
        this.cargarSegmentos();
        this.cargarCarpetas();
        if (succeeded > 0 && failed === 0) {
          this.toast.success({
            title: 'Segmentos movidos',
            message: `${succeeded === 1 ? '1 segmento movido' : `${succeeded} segmentos movidos`} correctamente.`,
          });
        } else if (succeeded > 0 && failed > 0) {
          this.toast.error({
            title: 'Movimiento parcial',
            message: `${succeeded} movido(s), ${failed} no se pudo(n) mover.`,
          });
        } else {
          this.toast.error({ title: 'Error al mover', message: 'No se pudieron mover los segmentos seleccionados.' });
        }
      },
    });
  }

  moveSingleToFolder(segmentId: number, folderId: number | null): void {
    this.segmentService.moveToFolder(segmentId, folderId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.cargarSegmentos();
        this.cargarCarpetas();
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudo mover el segmento a la carpeta.' });
      },
    });
  }

  async eliminarCarpeta(id: number): Promise<void> {
    const folder = this.folders.find(f => f.id === id);
    const segmentCount = folder?.segmentCount ?? 0;

    if (segmentCount > 0) {
      const segLabel = segmentCount === 1 ? '1 segmento' : `${segmentCount} segmentos`;
      const ok = await this.confirmDialog.confirm({
        title: 'Eliminar carpeta',
        message:
          `Esta carpeta contiene ${segLabel}. Al confirmar, la carpeta y todos sus segmentos serán eliminados permanentemente.\n\n` +
          `Si no deseas eliminar los segmentos, cancela esta acción y muévelos a otra carpeta primero.`,
        confirmText: `Eliminar carpeta y ${segLabel}`,
        cancelText: 'Cancelar',
        variant: 'danger'
      });
      if (!ok) return;

      this.folderApi.deleteFolder(id, true).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.cargarCarpetas();
          this.cargarSegmentos();
          this.cargarContadores();
          this.toast.success({
            title: 'Carpeta eliminada',
            message: `Se eliminaron la carpeta y sus ${segLabel}.`
          });
        },
        error: (err) => {
          const msg = err?.error?.message || 'No se pudo eliminar la carpeta.';
          this.toast.error({ title: 'No se pudo eliminar', message: msg });
        }
      });
    } else {
      const ok = await this.confirmDialog.confirmDanger({
        title: 'Eliminar carpeta',
        message: '¿Estás seguro de que deseas eliminar esta carpeta vacía?'
      });
      if (!ok) return;

      this.folderApi.deleteFolder(id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.cargarCarpetas();
          this.toast.deleted('Carpeta');
        },
        error: (err) => {
          const msg = err?.error?.message || 'No se pudo eliminar la carpeta.';
          this.toast.error({ title: 'Error', message: msg });
        }
      });
    }
  }

  async editarCarpeta(folder: SegmentFolder): Promise<void> {
    const newName = await this.confirmDialog.prompt({
      title: 'Editar carpeta',
      message: 'Escribe el nuevo nombre para la carpeta.',
      placeholder: 'Nombre de la carpeta',
      defaultValue: folder.name,
      confirmText: 'Guardar',
      variant: 'info'
    });
    if (!newName || newName.trim() === folder.name.trim()) return;

    this.folderApi.updateFolder(folder.id, { bussinesId: this.businessId, name: newName.trim() }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.cargarCarpetas();
        this.cargarSegmentos();
        this.toast.updated('Carpeta');
      },
      error: (err) => {
        const msg = err?.error?.message || 'No se pudo actualizar el nombre de la carpeta.';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  openFolderModal(): void {
    this.isFolderModalOpen = true;
  }
  
  closeFolderModal(seGuardo: boolean = false) {
    this.isFolderModalOpen = false;
    if (seGuardo) {
      this.cargarCarpetas();
      this.cargarSegmentos();
    }
  }
}
